<template>
  <Headline :subtitle="'Zukunftstechnologien erkunden'">Innovation Lab</Headline>
  <div class="pt-4 md:pt-8 xl:pt-16 pb-32 lg:my-5">
    <div class="container mx-auto max-w-screen-lg 2xl:max-w-screen-xl px-4 lg:px-0 mb-8">
      
      <div class="lg:text-center">
        <p class="inline-block p max-w-2xl pb-4 sm:pb-8 md:pb-12 mx-auto"><i>"Innovation distinguishes between a leader and a follower."</i> ― Steve Jobs</p>
      </div>

      <div class="lg:flex">
        <div class="lg:w-7/12 lg:pr-32 lg:mt-20">
          <h3 class="h3 title">Raum für Experimente</h3>
          <p>Unser Innovation Lab ist der Ort, wo wir neue Technologien erforschen und zukunftsweisende Lösungen entwickeln. Hier setzen wir auf Experimente und kontinuierliches Lernen, um unsere Kunden mit cutting-edge Technologien zu unterstützen.</p>
          <ul class="checklist darkblue mb-4 2xl:mb-8" style="color: #214979;">
            <li>Erforschung neuer Technologien und Frameworks</li>
            <li>Prototyping und Proof of Concepts</li>
            <li>Kontinuierliche Innovation und Optimierung</li>
          </ul>
        </div>
        <div class="lg:w-5/12">
          <!-- <img src="./../assets/img/intro/innovation.webp" alt="Innovation Lab" /> -->
        </div>
      </div>
    </div>

    <Container>
      <h3 class="h1">Softwerkstatt <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="52" class="inline-block -mt-4 opacity-90 text-[#0291D8]"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg> Innovation</h3>
      <p class="px-4 sm:px-0 mb-4 sm:mb-12 w-full md:max-w-4xl mx-auto text-left sm:text-center">Unser Innovation Lab ist das Herzstück unserer Zukunftsorientierung. Hier erkunden wir neue Technologien, experimentieren mit modernen Ansätzen und entwickeln innovative Lösungen für die Herausforderungen von morgen.
      </p>
    </Container>

    <div class="container mx-auto max-w-screen-lg 2xl:max-w-screen-xl px-4 lg:px-0 mt-16">
      <h2 class="h1">Unsere Fokusgebiete</h2>
      <p>Im Innovation Lab konzentrieren wir uns auf Bereiche, die die digitale Zukunft prägen. Unser Team experimentiert mit künstlicher Intelligenz, Cloud-Technologien und modernen Entwicklungsansätzen, um unseren Kunden immer einen Schritt voraus zu sein.</p>
      <div class="flex">
        <div class="w-2/3 z-10 mr-8 border border-gray-200 rounded-md shadow-sm bg-white">
          <!-- <img class="rounded-lg" src="./../assets/img/intro/innovation.webp" alt="Innovation Lab Fokusgebiete" /> -->
        </div>
        <div class="w-1/3 border border-gray-200 rounded-md shadow-sm bg-white z-10 px-2 md:px-4 lg:px-6 pt-4 lg:pt-8 ">
          <div class="-mx-6 px-6 pb-4 border-b-2" style="border-color: #428BB0">
            <img src="./../assets/img/icons/werkzeug.svg" alt="" class="w-12 inline-block mr-6 -mt-4" />
            <h3 class="h3 inline-block" style="color: #214A7B;">Über das Lab</h3>
          </div>
          <p>Das Innovation Lab ist unsere Forschungs- und Entwicklungsplattform. Hier darf experimentiert werden, neue Ideen werden getestet und Grenzen werden ausgelotet. Daraus entstehen innovative Lösungen für unsere Kunden.</p>
        </div>
      </div>
    </div>

    <Container class="-mt-28 sm:-mt-28 mb-4 sm:mb-8 2xl:mb-16" :styles="'py-6 sm:pb-8 pt-28 sm:pt-32 md:pt-36 lg:pt-48 px-4 lg:px-0'">
      <h2 class="h1">Was wir hier leisten</h2>
      <p class="max-w-5xl">Das Innovation Lab ist der Schnittpunkt zwischen klassischer Softwareentwicklung und zukunftsorientierten Technologien. Hier kombinieren wir unser Handwerk mit Innovationskraft, um unseren Kunden Lösungen anbieten zu können, die heute noch nicht überall selbstverständlich sind.</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-16 text-left mb-8">
        <div class="col-span-1 bg-white border border-gray-200 shadow-sm rounded-md p-2 lg:p-6 lg:pt-8">
          <div class="-mx-6 px-6 pb-4 border-b-2" style="border-color: #428BB0">
            <img src="./../assets/img/icons/konzept.svg" alt="" class="w-12 inline-block mr-6 -mt-4" />
            <h3 class="h3 inline-block" style="color: #214A7B;">Unsere Leistungen</h3>
          </div>
          <ul class="list">
            <li>Technologie-Recherche und Evaluation</li>
            <li>Rapid Prototyping und Proof of Concepts</li>
            <li>Integration neuer Technologien in bestehende Projekte</li>
            <li>Beratung bei Zukunftstechnologien</li>
          </ul>
        </div>
        <div class="col-span-1 bg-white border border-gray-200 shadow-sm rounded-md p-2 lg:p-6 lg:pt-8">
          <div class="-mx-6 px-6 pb-4 border-b-2" style="border-color: #428BB0">
            <img src="./../assets/img/icons/code.svg" alt="" class="w-12 inline-block mr-6 -mt-4" />
            <h3 class="h3 inline-block" style="color: #214A7B;">Unsere Umsetzung</h3>
          </div>
          <ul class="list">
            <li>Agile Experimentierzyklen</li>
            <li>Technologie-Stack Bewertung</li>
            <li>Roadmap-Entwicklung für Innovationsprojekte</li>
            <li>Von der Idee bis zur Produktionsreife</li>
          </ul>
        </div>
      </div>
    </Container>

    <div class="container mx-auto max-w-screen-lg 2xl:max-w-screen-xl px-4 lg:px-0">
      <ContactInfos />
    </div>
    
  </div>
</template>

<script>
import Headline from '../views/Headline.vue'
import ContactInfos from './../views/ContactInfos.vue'
import Container from './../views/Container.vue'

export default {
  name: 'InnovationLab',
  components: {
    Headline,
    ContactInfos,
    Container
  }
}
</script>

<style scoped>
</style>
